import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSiteScope } from '@/app/site/useSiteScope';
import { MembersPanel } from '@/features/team/MembersPanel';
import { usersListSiteMembers } from '@/shared/api/generated/users/users';
import { t } from '@/shared/i18n';
import { EmptyState } from '@/shared/ui/EmptyState';

import styles from './TeamPage.module.scss';

/**
 * Команда сайта: все, у кого есть к нему доступ, — роли на весь тенант и на
 * сам сайт — и приглашения.
 *
 * Виден тем, кто управляет людьми сайта. Администратор сайта раздаёт роли
 * только на этот сайт; роли на весь тенант — дело администратора тенанта, и
 * если первый попробует, отказ сервера будет показан у формы.
 */
export function TeamPage() {
  const scope = useSiteScope();
  const queryClient = useQueryClient();
  const allowed = scope?.can('people.manage') ?? false;
  const siteId = scope?.siteId ?? '';

  const query = useQuery({
    queryKey: ['site-members', siteId],
    enabled: allowed,
    queryFn: async () => {
      const response = await usersListSiteMembers(siteId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос команды');
      }

      return response.data;
    },
  });

  if (scope === null) {
    return null;
  }

  if (!scope.loading && !allowed) {
    return <EmptyState title={t.team.title} description={t.team.noAccess} />;
  }

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.team.title}</h1>
        <p className={styles['subtitle']}>{t.team.subtitle}</p>
      </header>

      {scope.site !== undefined && (
        <MembersPanel
          scope={{ kind: 'site', tenantID: scope.site.tenant_id, siteID: scope.siteId }}
          members={query.data?.members ?? []}
          invitations={query.data?.invitations ?? []}
          loading={query.isLoading}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['site-members', siteId] });
          }}
        />
      )}
    </div>
  );
}
